// The people on a call — one big face, or a grid of small tiles.
//
// A 1:1 keeps the single calm canvas it always had: one large avatar, the name,
// the timer. A group (Layer B5) gives everyone their own tile, built in the
// meeting room's visual language but smaller: rounded surface, speaking ring,
// name pinned to the bottom-left with the mic badge opposite.
//
// The box is what aligns the names. Centred labels under bare avatars drifted
// with the length of each name (and again with the mic icon beside it), so no
// two lined up; inside identical tiles they line up by construction.
//
// Anyone still being rung gets a dim tile of their own, so adding someone
// mid-call shows up as a person arriving rather than as nothing at all.
//
// Each tile subscribes to its OWN attendee slice (§9.3.1), so one person
// starting to talk repaints one tile — never the call screen.

import { memo } from 'react';

import { displayName } from '../lib/calls';
import { useCallStoreShallow } from '../store/callStore';
import { Avatar } from '../ui/Avatar';
import { IconMicOff } from '../ui/icons';

/** One person who has been rung but has not answered yet. */
export interface RingingPeer {
  id: string;
  name: string;
  avatar?: string;
}

/** Tile and avatar sizes per group size — roomy while there are few, tighter
 * as the call fills up to the 8-peer cap. */
function tileScale(n: number): { tile: number; avatar: number } {
  if (n <= 2) return { tile: 176, avatar: 84 };
  if (n <= 4) return { tile: 152, avatar: 70 };
  return { tile: 128, avatar: 58 };
}

/** The identities of everyone in the room but us. Strings, so the shallow
 * compare actually holds and the stage doesn't re-render on every audio tick. */
export function useRemoteIdentities(): string[] {
  return useCallStoreShallow((s) => s.order.filter((i) => i !== s.localIdentity));
}

export function CallStage({
  identities,
  ringing,
  /** Shown before anyone has answered on a 1:1 (name + photo from the ring). */
  fallbackName,
  fallbackAvatar,
}: {
  identities: string[];
  ringing: RingingPeer[];
  fallbackName: string;
  fallbackAvatar?: string;
}) {
  const total = identities.length + ringing.length;

  // A 1:1 that hasn't been answered yet: the person we called, waiting.
  if (total === 0 || (identities.length === 0 && ringing.length === 1)) {
    return (
      <div className="bz-acall-orb">
        <Avatar
          name={displayName(fallbackName)}
          identity={ringing[0]?.id ?? 'peer'}
          size={132}
          photoUrl={ringing[0]?.avatar ?? fallbackAvatar}
        />
      </div>
    );
  }
  if (total === 1 && identities.length === 1) {
    return <SoloPeer identity={identities[0]!} />;
  }

  const { tile, avatar } = tileScale(total);
  const style = { width: `${tile}px`, height: `${tile}px` };
  return (
    <div className="bz-acall-grid">
      {identities.map((id) => (
        <PeerTile key={id} identity={id} size={avatar} style={style} />
      ))}
      {ringing.map((p) => (
        <div className="bz-acalltile bz-acalltile-ringing" key={p.id} style={style}>
          <Avatar name={displayName(p.name)} identity={p.id} size={avatar} />
          <div className="bz-acalltile-foot">
            <span className="bz-acalltile-name">{displayName(p.name)}</span>
            <span className="bz-acalltile-note">Ringing…</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/** The 1:1 stage: one large avatar with the speaking halo, no box. */
const SoloPeer = memo(function SoloPeer({ identity }: { identity: string }) {
  const a = usePeer(identity);
  return (
    <div className={a.speaking ? 'bz-acall-orb bz-acall-orb-speaking' : 'bz-acall-orb'}>
      <Avatar name={displayName(a.name)} identity={identity} size={132} photoUrl={a.avatarUrl} />
    </div>
  );
});

/** One participant in a group: the meeting tile, shrunk to call proportions. */
const PeerTile = memo(function PeerTile({
  identity,
  size,
  style,
}: {
  identity: string;
  size: number;
  style: { width: string; height: string };
}) {
  const a = usePeer(identity);
  const name = displayName(a.name);

  return (
    <div
      className={a.speaking ? 'bz-acalltile bz-acalltile-speaking' : 'bz-acalltile'}
      style={style}
    >
      <Avatar name={name} identity={identity} size={size} photoUrl={a.avatarUrl} />
      <div className="bz-acalltile-foot">
        <span className="bz-acalltile-name">{name}</span>
        {!a.micEnabled && (
          <span className="bz-acalltile-muted" role="img" aria-label="Muted" title="Muted">
            <IconMicOff size={13} />
          </span>
        )}
      </div>
    </div>
  );
});

/** The slice a tile needs — primitives only, so the shallow compare holds. */
function usePeer(identity: string) {
  return useCallStoreShallow((s) => {
    const at = s.attendees[identity];
    return {
      name: at?.name ?? '',
      speaking: at?.isSpeaking ?? false,
      avatarUrl: at?.avatarUrl,
      micEnabled: at?.micEnabled ?? true,
    };
  });
}

/** The 1:1 title: the other person's name, once they are actually there. */
export function usePeerTitle(identities: string[], fallback: string): string {
  const first = useCallStoreShallow((s) =>
    identities[0] ? (s.attendees[identities[0]]?.name ?? '') : '',
  );
  return displayName(first || fallback);
}
